import { currentCycle, cycleNoticeHtml } from './cycle-facet';
import { canonicalPath } from '@electinfo/common';

// The GraphQL documents in leaf-profiles.ts write this limit literally (first: 20):
// they are fixed persisted documents (sites#277). Change both together.
export const RELATIONSHIP_LIMIT = 20;
export const MAX_LIVE_DEGREE = 50_000;

interface GraphQLError {
  message: string;
  extensions?: { code?: string };
}

/**
 * The API refused premium data (FORBIDDEN): voter and individual donor records
 * need a premium visitor token (sites#277). Pages show premiumRequiredHtml in
 * place of that data.
 */
export class PremiumRequiredError extends Error {}

interface GraphQLResponse<T> {
  data?: T;
  errors?: GraphQLError[];
}

interface ResolveResponse {
  entity_id?: string | null;
  canonical_path?: string;
}

export interface DetailSection {
  id: string;
  title: string;
  html: string;
}

export function matchLeafDetailPath(collection: string): string | null {
  const prefix = `/${collection}/`;
  if (!window.location.pathname.startsWith(prefix)) return null;
  const path = window.location.pathname.slice(prefix.length).replace(/\/$/, '');
  if (!path) return null;
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
}

export interface LeafOptions {
  /** Ask /_resolve for canonical metadata (default). Off for collections with no page_alias rows. */
  resolve?: boolean;
}

export function beginLeafMetadata(collection: string, id: string, options: LeafOptions = {}): Promise<string | null> {
  upsertMeta('robots', 'noindex, follow');
  setCanonical(storageCanonical(collection, id));
  return options.resolve === false ? Promise.resolve(null) : resolveCanonical(collection);
}

export function beginLeafDetail(collection: string, id: string, options: LeafOptions = {}): Promise<string | null> {
  loadEntityStylesheet();
  const resolution = beginLeafMetadata(collection, id, options);

  document.title = `Loading ${collection} - elect.info`;
  document.body.innerHTML = `
    <header>
      <h1>Loading...</h1>
      <p class="subtitle">Loading profile</p>
    </header>
    <section><p class="meta">Fetching current data...</p></section>
  `;
  return resolution;
}

export async function graphql<T>(
  query: string,
  variables: Record<string, unknown>,
  endpoint = '/api/graphql'
): Promise<T> {
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables })
  });

  if (!response.ok) {
    throw new Error(`GraphQL request failed (${response.status})`);
  }

  const payload = await response.json() as GraphQLResponse<T>;
  if (payload.errors?.some(e => e.extensions?.code === 'FORBIDDEN')) {
    throw new PremiumRequiredError(payload.errors[0].message);
  }
  if (payload.errors?.length) {
    throw new Error(payload.errors[0].message);
  }
  if (!payload.data) {
    throw new Error('GraphQL returned no data');
  }
  return payload.data;
}

export function renderDetailPage(
  title: string,
  subtitle: string,
  sections: DetailSection[],
  collection: string,
  browseLabel: string
): void {
  document.title = `${title} - elect.info`;
  const content = sections.map(section => `
    <section id="${escapeHtml(section.id)}">
      <h2>${escapeHtml(section.title)}</h2>
      ${section.html}
    </section>
  `).join('');

  // The identity bar (sites#355) may already be on the page: keep it under the new header.
  const bar = document.getElementById('auth-slot');
  document.body.innerHTML = `
    <header class="leaf-detail-page">
      <h1>${escapeHtml(title)}</h1>
      <p class="subtitle">${escapeHtml(subtitle)}</p>
    </header>
    ${cycleNoticeHtml(currentCycle())}
    ${content}
    <footer><p><a href="https://us.elect.info/">elect.info</a> | <a href="/${escapeHtml(collection)}/">${escapeHtml(browseLabel)}</a></p></footer>
  `;
  if (bar) document.querySelector('header')!.insertAdjacentElement('afterend', bar);
}

export function renderDetailError(entityLabel: string, message: string): void {
  document.title = `${entityLabel} Not Found - elect.info`;
  document.body.innerHTML = `
    <header class="leaf-detail-page">
      <h1>${escapeHtml(entityLabel)} Not Found</h1>
      <p class="subtitle">The requested profile could not be loaded.</p>
    </header>
    <section><p>${escapeHtml(message)}</p></section>
  `;
}

/** In place of premium data: what it is, a log-in link back to this page, and the premium contact. */
export function premiumRequiredHtml(what: string): string {
  const login = `/outpost.goauthentik.io/start?rd=${encodeURIComponent(window.location.href)}`;
  return `<p class="premium-required">${escapeHtml(what)} are premium data, available with an elect.info premium account.
    <a class="premium-login" href="${escapeHtml(login)}">Log in</a>, or contact
    <a href="mailto:premium@elect.info">premium@elect.info</a> for access.</p>`;
}

/** A whole detail page that is premium data. */
export function renderPremiumRequired(entityLabel: string, what: string): void {
  document.title = `${entityLabel} - elect.info`;
  document.body.innerHTML = `
    <header class="leaf-detail-page">
      <h1>${escapeHtml(entityLabel)}</h1>
      <p class="subtitle">Premium data</p>
    </header>
    <section>${premiumRequiredHtml(what)}</section>
  `;
}

export function definitionList(rows: Array<[string, unknown]>): string {
  return `<dl>${rows.map(([label, value]) => `
    <dt>${escapeHtml(label)}</dt>
    <dd>${displayValue(value)}</dd>
  `).join('')}</dl>`;
}

export function entityLink(entityType: string, id: string | null | undefined, label: unknown): string {
  const safeLabel = displayValue(label);
  if (!id) return safeLabel;
  return `<a href="/${escapeHtml(canonicalPath(entityType, id))}/">${safeLabel}</a>`;
}

/** Like definitionList, but values are already-escaped HTML. */
export function htmlList(rows: Array<[string, string]>): string {
  return `<dl>${rows.map(([label, html]) => `
    <dt>${escapeHtml(label)}</dt>
    <dd>${html}</dd>
  `).join('')}</dl>`;
}

export function link(href: string | null, label: string | null | undefined): string {
  return href && label ? `<a href="${escapeHtml(href)}">${escapeHtml(label)}</a>` : displayValue(label);
}

export function titleCase(value: string | null | undefined): string {
  return (value || '').toLowerCase().replace(/\b[a-z]/g, c => c.toUpperCase());
}

/** "FL-11" -> /districts/fl-11/ (the canonical district path). */
export function districtPath(code: string | null | undefined): string | null {
  const match = /^([A-Z]{2})-(\d{1,2}|AL)$/i.exec((code || '').trim());
  if (!match) return null;
  const number = /^\d+$/.test(match[2]) ? match[2].padStart(2, '0') : '00';
  return `/districts/${match[1].toLowerCase()}-${number}/`;
}

/** /states/tx/ for a two-letter state code. */
export function statePath(code: string | null | undefined): string | null {
  const st = (code || '').trim();
  return /^[A-Z]{2}$/i.test(st) ? `/states/${st.toLowerCase()}/` : null;
}

/**
 * /<collection>/?f_state=NY&f_<key>=value: an index page filtered to one exact
 * value (filter-bar URL format). The state filter rides along unless it is the key.
 */
export function filterPath(
  collection: string,
  state: string | null | undefined,
  key: string,
  value: string | null | undefined
): string | null {
  const v = (value || '').trim();
  if (!v) return null;
  const params = new URLSearchParams();
  const st = (state || '').trim().toUpperCase();
  if (st && key !== 'state') params.set('f_state', st);
  params.set(`f_${key}`, v);
  return `/${collection}/?${params.toString()}`;
}

/** A row value plus a secondary link (e.g. "voters", "donors") to a filtered index page. */
export function withFilterLink(primary: string, href: string | null, label: string, className: string): string {
  return href
    ? `${primary} <a class="meta ${escapeHtml(className)}" href="${escapeHtml(href)}">${escapeHtml(label)}</a>`
    : primary;
}

/**
 * One Location card value: the entity page when the graph has it, plus a
 * secondary link to the filtered list ("donors", "voters"); with no entity
 * page, the label itself opens the filtered list.
 */
export function locationValue(
  label: string | null | undefined,
  entityHref: string | null,
  filterHref: string | null,
  noun: string,
  className: string
): string {
  if (!label) return displayValue(null);
  return entityHref
    ? withFilterLink(link(entityHref, label), filterHref, noun, className)
    : link(filterHref, label);
}

const COUNTY_SUFFIXES = ['COUNTY', 'PARISH', 'BOROUGH', 'CITY AND BOROUGH', 'CENSUS AREA', 'MUNICIPALITY', 'MUNICIPIO'];

/**
 * The candidate County named by a bare county string, as voter files write it
 * ("NORFOLK" -> "Norfolk County", "ORLEANS" -> "Orleans Parish"); null when
 * none is. Never a different kind of entity with the same stem ("Richmond city").
 */
export function matchCountyName<T extends { name: string | null }>(county: string | null | undefined, candidates: T[]): T | null {
  const key = (county || '').trim().toUpperCase().replace(/\s+COUNTY$/, '');
  if (!key) return null;
  return candidates.find(c => {
    const name = (c.name || '').trim().toUpperCase();
    return name === key || COUNTY_SUFFIXES.some(suffix => name === `${key} ${suffix}`);
  }) || null;
}

export function relationshipTable(
  headers: string[],
  rows: string[][],
  numericColumns: number[] = []
): string {
  if (!rows.length) {
    return '<p class="meta leaf-relationship-empty">No related records found.</p>';
  }
  const head = headers.map((header, index) =>
    `<th${numericColumns.includes(index) ? ' class="num"' : ''}>${escapeHtml(header)}</th>`
  ).join('');
  const body = rows.map(row => `<tr>${row.map((cell, index) =>
    `<td${numericColumns.includes(index) ? ' class="num"' : ''}>${cell}</td>`
  ).join('')}</tr>`).join('');
  return `<table class="breakdown-table leaf-relationship-list"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

/**
 * "Showing top N of P committees" or "Showing all P committees", as the
 * pages.elect.info ListFooter (HtmlComponents.listFooter) renders it.
 */
export function listFooter(shown: number, total: number | null | undefined, noun = ''): string {
  const all = Math.max(Number(total) || 0, shown);
  const suffix = noun ? ` ${noun}` : '';
  const text = all > shown
    ? `Showing top ${formatCount(shown)} of ${formatCount(all)}${suffix}`
    : `Showing all ${formatCount(all)}${suffix}`;
  return `<p class="meta leaf-list-footer">${escapeHtml(text)}</p>`;
}

export function deferredRelationshipMessage(degree: number): string {
  return `
    <p class="meta leaf-hub-deferred">
      This profile has ${formatCount(degree)} related records. Totals are available above;
      the top-record list is deferred until the ranked-edge index is available.
    </p>
  `;
}

export function formatCurrency(value: unknown): string {
  const amount = toFiniteNumber(value);
  if (amount === null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2
  }).format(amount);
}

export function formatCount(value: unknown): string {
  const count = toFiniteNumber(value);
  return count === null ? '—' : Math.round(count).toLocaleString('en-US');
}

export function formatPercent(value: unknown): string {
  const ratio = toFiniteNumber(value);
  return ratio === null ? '—' : `${(ratio * 100).toFixed(1)}%`;
}

export function formatDate(value: unknown): string {
  if (typeof value !== 'string' || !value) return '—';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return escapeHtml(value);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return escapeHtml(String(value));
}

export function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function loadEntityStylesheet(): void {
  const existing = document.querySelector<HTMLLinkElement>('link[href="/css/style.css"]');
  if (existing) {
    existing.href = '/css/entity.css';
  } else if (!document.querySelector('link[href="/css/entity.css"]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/css/entity.css';
    document.head.appendChild(link);
  }
  document.querySelectorAll('head style').forEach(style => style.remove());
}

function upsertMeta(name: string, content: string): void {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function setCanonical(href: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}

function storageCanonical(collection: string, id: string): string {
  const path = `/${collection}/${encodeURIComponent(id)}/`;
  return new URL(path, 'https://us.elect.info').toString();
}

async function resolveCanonical(collection: string): Promise<string | null> {
  const segments = window.location.pathname.split('/').filter(Boolean);
  const path = segments.slice(1).join('/');
  if (!path) return null;

  try {
    const response = await fetchResolution(collection, path);
    const direct = await readResolution(response);
    if (direct) return applyResolution(direct);

    // Alias rows redirect to the canonical site URL even for JSON clients.
    // Follow that path back through /_resolve to obtain the entity id as well
    // as the canonical path. Fetch follows the first 301 automatically.
    if (!response.redirected) return null;
    const canonicalUrl = new URL(response.url);
    if (canonicalUrl.origin !== window.location.origin) return null;
    const canonicalSegments = canonicalUrl.pathname.split('/').filter(Boolean);
    if (canonicalSegments[0]?.toLowerCase() !== collection.toLowerCase()) return null;
    const canonicalPath = canonicalSegments.slice(1).join('/');
    if (!canonicalPath) return null;

    setCanonical(new URL(canonicalUrl.pathname, 'https://us.elect.info').toString());
    const canonicalResponse = await fetchResolution(collection, canonicalPath);
    const resolved = await readResolution(canonicalResponse);
    return resolved ? applyResolution(resolved) : null;
  } catch {
    // Phase 1 may not be deployed yet. The storage-path canonical remains valid.
    return null;
  }
}

function fetchResolution(collection: string, path: string): Promise<Response> {
  return fetch(`/_resolve/${encodeURIComponent(collection)}/${path}`, {
    headers: { Accept: 'application/json' }
  });
}

async function readResolution(response: Response): Promise<ResolveResponse | null> {
  if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) return null;
  return response.json() as Promise<ResolveResponse>;
}

function applyResolution(resolved: ResolveResponse): string | null {
  if (resolved.canonical_path) {
    setCanonical(new URL(resolved.canonical_path, 'https://us.elect.info').toString());
  }
  return resolved.entity_id || null;
}
